import React, { useState } from "react";
import { PlayCircle, Heart, Waves, Sparkles, Clock, Eye, Music, Moon, Leaf, Flame, Mountain } from "lucide-react";
import InnateAbilitiesMeditation from "@/components/InnateAbilitiesMeditation";
import HeartCoherenceMeditation from "@/components/HeartCoherenceMeditation";
import VipassanaMeditation from "@/components/VipassanaMeditation";
import LovingKindnessMeditation from "@/components/LovingKindnessMeditation";
import YogaNidraMeditation from "@/components/YogaNidraMeditation";
import MeditationChapterDivider from "@/components/MeditationChapterDivider";
import { EDITORIAL } from "@/lib/editorialImages";

/**
 * MeditationTab — three-category library.
 *
 * All four meditations exist in three variants selected by duration:
 *   • Beginner     — 5 minutes.  Condensed script, gentle onboarding.
 *   • Intermediate — 10 minutes. The full traditional script.
 *   • Advanced     — 20 minutes. Extended script with longer practice space.
 *
 * Each overlay component (Heart-Coherence, Vipassana, Loving-Kindness,
 * Yoga-Nidra) accepts a `variant` prop that selects the appropriate
 * beat sequence. Tapping any card sets the open state to a single
 * shared object { kind, variant } and the correct overlay renders.
 */

// Card metadata — everything the UI needs to render, keyed by meditation kind.
const KINDS = {
  innateAbilities: {
    label: "Remembering Your Innate Abilities",
    Icon: Sparkles,
    accent: "hsl(var(--terracotta))",
    accentBg: "bg-[hsl(var(--terracotta))]/15 text-[hsl(var(--terracotta))]",
    buttonBg: "bg-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta))]/90",
    tagline: "Remember what has always been yours.",
    detail: "A clear return to five innate capacities you were born with — regulation, intuition, emotional intelligence, healing, and expansion — held in a warm ambient field.",
  },
  coherence: {
    label: "Brain–Heart Coherence",
    Icon: Heart,
    accent: "hsl(var(--terracotta))",
    accentBg: "bg-[hsl(var(--terracotta))]/15 text-[hsl(var(--terracotta))]",
    buttonBg: "bg-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta))]/90",
    tagline: "Breathe through the heart. Anchor in wholeness.",
    detail: "Slow 5-in / 5-out breath through the heart, a steady feeling of ease, and the affirmation \"I am whole\" to anchor coherence.",
  },
  vipassana: {
    label: "Vipassana",
    Icon: Eye,
    accent: "hsl(var(--fg))",
    accentBg: "bg-[hsl(var(--clay))]/12 text-[hsl(var(--clay))]",
    buttonBg: "bg-[hsl(var(--fg))] hover:bg-[hsl(var(--fg))]/90",
    tagline: "See things as they are.",
    detail: "Observe the breath without changing it. Note each sensation rising and falling. Insight through steady noticing.",
  },
  lovingKindness: {
    label: "Loving-Kindness · Metta",
    Icon: Heart,
    accent: "hsl(var(--terracotta))",
    accentBg: "bg-[hsl(var(--terracotta))]/18 text-[hsl(var(--terracotta))]",
    buttonBg: "bg-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta))]/90",
    tagline: "Warmth for yourself and the world.",
    detail: "The classic Metta arc — may I be safe, may all beings be loved — carried on a warm 432 Hz healing tone.",
  },
  yogaNidra: {
    label: "Yoga Nidra · Deep Rest",
    Icon: Moon,
    accent: "hsl(230, 30%, 42%)",
    accentBg: "bg-[hsl(230,25%,60%)]/15 text-[hsl(230,30%,42%)]",
    buttonBg: "bg-[hsl(230,30%,32%)] hover:bg-[hsl(230,30%,26%)]",
    tagline: "Rotate awareness. Rest deeply.",
    detail: "The traditional Nidra flow — settle, soften, follow the breath, rotate awareness through the body, deep spacious rest.",
  },
};

const CATEGORIES = [
  {
    variant: "beginner",
    label: "Beginner",
    minutes: 5,
    Icon: Leaf,
    hint: "Short and steady. A clear way to begin.",
    chapter: {
      eyebrow: "ARRIVE",
      line: "Begin where you are.",
      // Assigned via `editorialImages.js` — sole owner across the app.
      image: EDITORIAL.meditationArrive,
      alt: "Still water catching warm dawn light — a quiet arrival.",
    },
  },
  {
    variant: "intermediate",
    label: "Intermediate",
    minutes: 10,
    Icon: Flame,
    hint: "The full traditional length. A daily practice.",
    chapter: {
      eyebrow: "DEEPEN",
      line: "Stay long enough to hear what quiet reveals.",
      image: EDITORIAL.meditationDeepen,
      alt: "Filtered light through a misted forest — moving inward.",
    },
  },
  {
    variant: "advanced",
    label: "Advanced",
    minutes: 20,
    Icon: Mountain,
    hint: "Extended pauses and deeper practice space.",
    chapter: {
      eyebrow: "EXPAND",
      line: "Go beyond the noise. Return to what remains.",
      image: EDITORIAL.meditationExpand,
      alt: "Layered mountain ridges receding into atmospheric light — expansion.",
    },
  },
];

export default function MeditationTab() {
  // One shared open state — { kind, variant } — so exactly one overlay is
  // ever open at a time.
  const [open, setOpen] = useState(null);
  const close = () => setOpen(null);

  return (
    <section className="space-y-16 reveal reveal-1" data-testid="meditation-tab">
      <header>
        <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--terracotta))] inline-flex items-center gap-2">
          <Sparkles size={12} /> Meditation
        </div>
        <h2 className="font-display text-3xl md:text-4xl mt-3 tracking-tight italic leading-tight">
          Quiet the mind. Meet the heart.
        </h2>
        <p className="mt-3 max-w-2xl text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
          A library of guided meditations at three durations. Each one holds you in a steady rhythm while N8 reads the script clearly and a warm ambient tone breathes beneath.
        </p>
      </header>

      {CATEGORIES.map((cat) => (
        <section
          key={cat.variant}
          className="space-y-6"
          data-testid={`meditation-category-${cat.variant}`}
        >
          {/* Editorial chapter divider — one shared visual system across
              the three levels. The photograph carries the emotional shift
              (ARRIVE → DEEPEN → EXPAND); everything else stays identical. */}
          <MeditationChapterDivider
            eyebrow={cat.chapter.eyebrow}
            line={cat.chapter.line}
            image={cat.chapter.image}
            alt={cat.chapter.alt}
            testid={`meditation-chapter-${cat.variant}`}
          />

          <div className="flex items-baseline gap-3 border-b hairline pb-2 pt-2">
            <cat.Icon size={14} className="text-[hsl(var(--clay))]" />
            <h3 className="font-display text-xl tracking-tight text-[hsl(var(--fg))]">
              {cat.label}
              <span className="ml-3 text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--muted-foreground))]">
                {cat.minutes} minutes
              </span>
            </h3>
            <span className="text-[11px] italic text-[hsl(var(--muted-foreground))]">— {cat.hint}</span>
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-2">
            {Object.entries(KINDS).map(([kind, meta]) => (
              <MeditationCard
                key={`${cat.variant}-${kind}`}
                kind={kind}
                meta={meta}
                variant={cat.variant}
                minutes={cat.minutes}
                onOpen={() => setOpen({ kind, variant: cat.variant })}
              />
            ))}
          </div>
        </section>
      ))}

      {/* Overlays — only render the one that matches. */}
      <InnateAbilitiesMeditation
        open={open?.kind === "innateAbilities"}
        variant={open?.variant}
        onClose={close}
      />
      <HeartCoherenceMeditation
        open={open?.kind === "coherence"}
        variant={open?.variant}
        onClose={close}
      />
      <VipassanaMeditation
        open={open?.kind === "vipassana"}
        variant={open?.variant}
        onClose={close}
      />
      <LovingKindnessMeditation
        open={open?.kind === "lovingKindness"}
        variant={open?.variant}
        onClose={close}
      />
      <YogaNidraMeditation
        open={open?.kind === "yogaNidra"}
        variant={open?.variant}
        onClose={close}
      />
    </section>
  );
}

function MeditationCard({ kind, meta, variant, minutes, onOpen }) {
  const testid = `meditation-card-${kind}-${variant}`;
  const beginTestid = `meditation-begin-${kind}-${variant}`;
  return (
    <article
      className="relative overflow-hidden rounded-md border hairline bg-[hsl(var(--paper))] p-6"
      data-testid={testid}
    >
      <div className="flex items-start gap-4">
        <span
          aria-hidden
          className={`grid place-items-center w-10 h-10 rounded-full shrink-0 ${meta.accentBg}`}
        >
          <meta.Icon size={16} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--clay))]">
            {meta.label}
          </div>
          <h4 className="mt-2 font-display text-xl leading-tight text-[hsl(var(--fg))]">
            {meta.tagline}
          </h4>
          <p className="mt-2 text-[13px] text-[hsl(var(--muted-foreground))] leading-relaxed">
            {meta.detail}
          </p>
          <div className="mt-4 flex items-center justify-between gap-2">
            <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">
              <Clock size={11} /> {minutes} min
            </span>
            <button
              type="button"
              onClick={onOpen}
              data-testid={beginTestid}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--paper))] tab-link ${meta.buttonBg}`}
            >
              <PlayCircle size={12} />
              Begin
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}
